// Every write the app makes. Saves are optimistic: the screen updates at once, then rolls back if the database says no.
import type { Patch, Task } from '@shared/index';
import { nextStageOrder, NEW_TRACKING } from '@shared/index';
import { ReadOnlyError, type Backend } from './backend/types';
import { findRow, isStageId, removeRow, store, upsertRow } from './store';
import { render, renderSoon } from './render';
import { $, toast } from './ui';

let backend: Backend;
export function useBackend(b: Backend) { backend = b; }
export const getBackend = () => backend;

function failed(e: unknown, fallback: string) {
  if (e instanceof ReadOnlyError) {
    store.canWrite = false;
    $('ro').hidden = false;
    render();
    toast('You can’t edit this tracker');
  } else {
    console.error(e);
    toast(fallback);
  }
}

/** Save a change to a job (by quote) or a stage (by id). */
export async function save(key: string, patch: Patch) {
  const table = isStageId(key) ? 'stages' : 'jobs';
  const row = findRow(table, key);
  if (!row) return;
  const before = { ...row };
  upsertRow(table, { ...row, ...patch });
  renderSoon();
  try {
    const saved = table === 'stages' ? await backend.updateStage(key, patch) : await backend.updateJob(key, patch);
    upsertRow(table, saved as unknown as Record<string, unknown>);
    renderSoon();
    toast('Saved');
  } catch (e) {
    upsertRow(table, before);
    renderSoon();
    failed(e, 'Couldn’t save. Try again.');
  }
}

export async function addStage(parent: string, name: string) {
  try {
    const st = await backend.insertStage({ parent_quote: parent, name, sort_order: nextStageOrder(store.stages, parent), ...NEW_TRACKING });
    upsertRow('stages', st as unknown as Record<string, unknown>);
    renderSoon();
    toast('Stage added');
  } catch (e) { failed(e, 'Couldn’t add the stage. Try again.'); }
}

export async function removeStage(id: string) {
  try {
    await backend.deleteStage(id);
    removeRow('stages', id);
    renderSoon();
    toast('Stage removed');
  } catch (e) { failed(e, 'Couldn’t remove. Try again.'); }
}

export async function addTask(t: Pick<Task, 'text' | 'pm' | 'quote' | 'due'>) {
  try {
    const row = await backend.insertTask({ ...t, done: false });
    upsertRow('tasks', row as unknown as Record<string, unknown>);
    renderSoon();
    toast('Task added');
  } catch (e) { failed(e, 'Couldn’t add the task. Try again.'); }
}

export async function saveTask(id: string, patch: Partial<Task>) {
  const row = findRow('tasks', id);
  if (!row) return;
  const before = { ...row };
  upsertRow('tasks', { ...row, ...patch, ...(patch.done !== undefined ? { done_at: patch.done ? new Date().toISOString() : null } : {}) });
  renderSoon();
  try {
    const saved = await backend.updateTask(id, patch);
    upsertRow('tasks', saved as unknown as Record<string, unknown>);
    renderSoon();
  } catch (e) {
    upsertRow('tasks', before);
    renderSoon();
    failed(e, 'Couldn’t save. Try again.');
  }
}

export async function deleteTask(id: string) {
  try {
    await backend.deleteTask(id);
    removeRow('tasks', id);
    renderSoon();
    toast('Task deleted');
  } catch (e) { failed(e, 'Couldn’t delete. Try again.'); }
}
